import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { localizeApiError } from "@/i18n/apiErrors";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, Lock, User, ChefHat, ArrowRight, ArrowLeft, Globe } from "lucide-react";

const LANG_FLAGS = { uk: "🇺🇦", en: "🇬🇧", ru: "🇷🇺" };

export default function AuthPage() {
  // Landing and the demo CTA both link to /auth?mode=signup. The parameter was
  // never read, so everyone arriving to create an account opened on Sign in
  // and had to find the switch link at the bottom first.
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get("mode") !== "signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [emailTaken, setEmailTaken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { login, register } = useAuth();
  const { t, lang, switchLang } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailTaken(false);
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name, lang, termsAccepted);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 400 && detail === "Email already registered") {
        setEmailTaken(true);
        setError("");
      } else if (err.response?.status === 429) {
        setError(t("errRateLimit"));
      } else {
        setError(localizeApiError(detail, t));
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col items-center justify-center px-4" data-testid="auth-page">
      {/* Back to the public site. Someone who arrives here from an ad, a
          bookmark or a logout has no way out otherwise: this screen has no
          header, and the logo is not a link. A full navigation rather than a
          <Link>: the marketing page and the app are separate route trees. */}
      <a
        href="/"
        data-testid="auth-back-home"
        className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-2 bg-white rounded-full border border-[#E8E5E0] font-body text-sm text-[#6B705C] hover:border-[#7B9E7F] hover:text-[#4A6B4E] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        {t("authBackHome")}
      </a>

      {/* Language Switcher - top right */}
      <div className="absolute top-4 right-4">
        <button
          data-testid="auth-lang-btn"
          onClick={() => setShowLangPicker(!showLangPicker)}
          className="flex items-center gap-1.5 px-3 py-2 bg-white rounded-full border border-[#E8E5E0] font-body text-sm text-[#6B705C] hover:border-[#7B9E7F] transition-colors"
        >
          <span className="text-base">{LANG_FLAGS[lang]}</span>
          <Globe className="w-4 h-4" strokeWidth={1.5} />
        </button>
        {showLangPicker && (
          <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg border border-[#E8E5E0] overflow-hidden z-50">
            {Object.entries(LANG_FLAGS).map(([code, flag]) => (
              <button
                key={code}
                data-testid={`auth-lang-${code}`}
                onClick={() => { switchLang(code); setShowLangPicker(false); }}
                className={`w-full px-4 py-2.5 flex items-center gap-2 font-body text-sm hover:bg-[#F2E9E4] transition-colors ${
                  lang === code ? "bg-[#7B9E7F]/10 text-[#4A6B4E] font-semibold" : "text-[#3D4035]"
                }`}
              >
                <span>{flag}</span>
                <span>{code === "uk" ? "Українська" : code === "en" ? "English" : "Русский"}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Hero section */}
      <div className="w-full max-w-md text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#7B9E7F] mb-6 shadow-lg shadow-[#7B9E7F]/20">
          <ChefHat className="w-10 h-10 text-white" strokeWidth={1.5} />
        </div>
        <h1 className="font-heading text-4xl text-[#3D4035] mb-2">Meal Pill</h1>
        <p className="font-body text-[#6B705C] text-lg">
          {isLogin ? t("welcomeBack") : t("welcome")}
        </p>
      </div>

      {/* Form card */}
      <div className="w-full max-w-md bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 border border-black/5">
        <form onSubmit={handleSubmit} className="space-y-5">
          {!isLogin && (
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A5A58D]" strokeWidth={1.5} />
              <input
                data-testid="auth-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("name")}
                className="w-full bg-white border-transparent ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-14 pl-12 pr-4 font-body text-[#3D4035] placeholder:text-[#A5A58D] outline-none transition-all"
                required
              />
            </div>
          )}

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A5A58D]" strokeWidth={1.5} />
            <input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("email")}
              className="w-full bg-white border-transparent ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-14 pl-12 pr-4 font-body text-[#3D4035] placeholder:text-[#A5A58D] outline-none transition-all"
              required
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A5A58D]" strokeWidth={1.5} />
            <input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("password")}
              className="w-full bg-white border-transparent ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-14 pl-12 pr-4 font-body text-[#3D4035] placeholder:text-[#A5A58D] outline-none transition-all"
              required
              minLength={8}
            />
          </div>
          {isLogin && (
            <div className="text-right -mt-1">
              <Link to="/forgot-password" className="text-sm text-[#7B9E7F] hover:underline font-body">
                {t("forgotPassword") || "Forgot password?"}
              </Link>
            </div>
          )}
          {error && (
            <div data-testid="auth-error" className="text-[#BC4749] text-sm font-body bg-[#BC4749]/10 rounded-xl px-4 py-3">
              {error}
            </div>
          )}
          {emailTaken && (
            <div data-testid="auth-email-taken" className="text-[#BC4749] text-sm font-body bg-[#BC4749]/10 rounded-xl px-4 py-3">
              <p>{t("emailAlreadyRegistered")}</p>
              <button
                type="button"
                data-testid="auth-goto-login"
                onClick={() => { setIsLogin(true); setEmailTaken(false); setError(""); }}
                className="mt-2 font-bold text-[#7B9E7F] underline underline-offset-4 hover:opacity-80 transition-opacity"
              >
                {t("goToLogin")}
              </button>
            </div>
          )}

          {!isLogin && (
            <label className="flex items-start gap-3 mb-4 cursor-pointer">
              <input
                data-testid="auth-terms-checkbox"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#7B9E7F] shrink-0"
              />
              <span className="font-body text-sm text-gray-600">
                {t("termsAgreePrefix")}{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#7B9E7F] underline">
                  {t("termsLinkLabel")}
                </a>{" "}
                {t("termsAgreeAnd")}{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#7B9E7F] underline">
                  {t("privacyLinkLabel")}
                </a>
              </span>
            </label>
          )}

          <button
            data-testid="auth-submit-button"
            type="submit"
            disabled={loading || (!isLogin && !termsAccepted)}
            className="w-full bg-[#7B9E7F] text-white rounded-full h-14 font-body font-bold text-lg hover:opacity-90 transition-all active:scale-[0.98] shadow-lg shadow-[#7B9E7F]/20 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                {isLogin ? t("login") : t("createAccount")}
                <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            data-testid="auth-switch-mode"
            onClick={() => { setIsLogin(!isLogin); setError(""); setEmailTaken(false); setTermsAccepted(false); }}
            className="font-body text-[#6B705C] hover:text-[#7B9E7F] transition-colors"
          >
            {isLogin ? t("noAccount") : t("hasAccount")}{" "}
            <span className="font-bold text-[#7B9E7F] underline underline-offset-4">
              {isLogin ? t("register") : t("login")}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
